/**
 * Vuex module — global state for the active export/remove batch and
 * its polling lifecycle.
 *
 * <h3>Why a Vuex module instead of a per-page poller</h3>
 * The export action ({@code POST /api/attendance/export/people} et al.)
 * returns a batchId; progress is then polled from
 * {@code /api/attendance/export/batch/:id/status} until {@code done:true}.
 * Originally the poller lived inside {@code exports.vue}, which meant
 * the moment a user clicked away from "Send to gadget" the poll loop
 * was destroyed and they lost visibility into a multi-minute push.
 *
 * Lifting the poll loop into a Vuex module makes the progress survive
 * navigation: the toast component (mounted globally in App.vue) reads
 * from this store and renders the bar wherever the user is in the app.
 *
 * <h3>Self-registration</h3>
 * This module is registered lazily by the global toast component via
 * {@code store.registerModule('exportProgress', module)} on mount, so
 * existing code in {@code store/index.js} doesn't need editing.
 * Subsequent mounts use the {@code preserveState} flag to keep an
 * in-flight batch alive across re-mounts (e.g. hot-reload).
 *
 * <h3>State shape</h3>
 * <pre>
 *   batch: null                        // no active batch
 *        | {
 *            id,                       // UUID returned by /export
 *            kind,                     // 'export' | 'remove'
 *            label,                    // user-facing label, e.g.
 *                                      //   "Sending 312 students to Front Gate"
 *            startedAt,                // Date.now() at submit
 *            // Live counts mirrored from /status:
 *            total, pending, dispatched, completed, failed,
 *            recentFailures: [...],
 *            done: false,
 *            // For UI:
 *            consecutivePollErrors: 0,
 *            dismissed: false,         // user X'd the toast post-completion
 *          }
 *   pollTimerId: number | null         // outstanding setTimeout id
 * </pre>
 */

const POLL_OK_MS   = 3000;
const POLL_ERR_MS  = 6000;
const ERR_THRESHOLD = 5;

const initial = () => ({
  batch: null,
  pollTimerId: null,
});

export default {
  namespaced: true,

  state: initial(),

  getters: {
    /** True when there's any progress worth showing in the toast. */
    isVisible(state) {
      return !!state.batch && !state.batch.dismissed;
    },
    /** 0–100 progress percentage; safe when total is 0. */
    percent(state) {
      const b = state.batch;
      if (!b || !b.total) return 0;
      const done = (b.completed || 0) + (b.failed || 0);
      return Math.min(100, Math.round((done / b.total) * 100));
    },
  },

  mutations: {
    /**
     * Initialise a new active batch. Replaces any prior one — the
     * caller is responsible for stopping the old timer (do that via
     * {@code dispatch('stop')} before {@code dispatch('start', ...)}).
     */
    SET_BATCH(state, batch) {
      state.batch = batch;
    },
    UPDATE_BATCH(state, patch) {
      if (!state.batch) return;
      Object.assign(state.batch, patch);
    },
    SET_POLL_TIMER(state, id) {
      state.pollTimerId = id;
    },
    CLEAR(state) {
      state.batch = null;
      state.pollTimerId = null;
    },
    DISMISS(state) {
      if (state.batch) state.batch.dismissed = true;
    },
  },

  actions: {
    /**
     * Begin tracking a batch. Payload comes straight from
     * ExportBatchResponse plus a kind/label the UI provides for
     * display. Kicks off the first poll immediately so the bar
     * moves on the same tick as the toast appears.
     */
    start({ state, commit, dispatch }, payload) {
      // Idempotency: if an unfinished batch with the same id is
      // already active, do nothing (avoids double-polling on a
      // rapid double-click).
      if (state.batch && state.batch.id === payload.batchId && !state.batch.done) {
        return;
      }
      // Replace any prior batch — most recent push wins.
      dispatch('stop');
      commit('SET_BATCH', {
        id: payload.batchId,
        kind: payload.kind || 'export',
        label: payload.label || (payload.kind === 'remove'
          ? 'Removing users from gadget(s)…'
          : 'Sending users to gadget(s)…'),
        startedAt: Date.now(),
        // Sync hints from the immediate response. /status will
        // overwrite these on the first poll.
        total: payload.queued || 0,
        pending: payload.queued || 0,
        dispatched: 0,
        completed: 0,
        failed: 0,
        recentFailures: [],
        done: false,
        consecutivePollErrors: 0,
        dismissed: false,
      });
      dispatch('poll');
    },

    /**
     * One-shot poll. Re-schedules itself via setTimeout until the
     * batch reports {@code done:true} or hits the consecutive-error
     * threshold.
     */
    async poll({ state, commit, dispatch }) {
      const batch = state.batch;
      if (!batch || batch.done) return;
      const id = batch.id;

      // Lazy-load apiService to avoid a circular import at module-load
      // time (the store may be imported before apiService finishes
      // wiring its interceptors).
      let apiService;
      try {
        apiService = (await import('@/apis/apiService')).default;
      } catch (e) {
        // No transport available — give up rather than crash the app.
        return;
      }

      try {
        const res = await apiService.apiClient.get(
          '/api/attendance/export/batch/' + encodeURIComponent(id) + '/status'
        );
        // Bail if the active batch changed under us mid-flight.
        if (!state.batch || state.batch.id !== id) return;
        const d = res.data || {};
        commit('UPDATE_BATCH', {
          total:          d.total          ?? batch.total,
          pending:        d.pending        ?? 0,
          dispatched:     d.dispatched     ?? 0,
          completed:      d.completed      ?? 0,
          failed:         d.failed         ?? 0,
          done:           !!d.done,
          recentFailures: Array.isArray(d.recentFailures) ? d.recentFailures : [],
          consecutivePollErrors: 0,
        });
        if (state.batch.done) return;
      } catch (_e) {
        if (!state.batch || state.batch.id !== id) return;
        commit('UPDATE_BATCH', {
          consecutivePollErrors: (state.batch.consecutivePollErrors || 0) + 1,
        });
        if (state.batch.consecutivePollErrors >= ERR_THRESHOLD) {
          // Stop polling; leave the toast visible so the user knows
          // something went wrong. They can dismiss it manually.
          return;
        }
      }

      const next = state.batch.consecutivePollErrors > 0 ? POLL_ERR_MS : POLL_OK_MS;
      const tid = setTimeout(() => dispatch('poll'), next);
      commit('SET_POLL_TIMER', tid);
    },

    /** Halt polling and forget any active batch. */
    stop({ state, commit }) {
      if (state.pollTimerId != null) {
        clearTimeout(state.pollTimerId);
      }
      commit('CLEAR');
    },

    /** User clicked the X on the toast. Keeps polling history but hides. */
    dismiss({ state, commit, dispatch }) {
      // If still in-flight, stop polling outright — the user said
      // "I don't care anymore". If already done, just hide.
      if (state.batch && !state.batch.done) {
        dispatch('stop');
      } else {
        commit('DISMISS');
      }
    },
  },
};
